import React from 'react';
import RMPicker from 'react-mobile-picker';
import { cn } from '@/lib/cn';

export type PickerOption = {
  value: any;
  label?: string;
};

export type PickerProps = {
  name: string;
  options: PickerOption[];
  value: string | number;
  onChange: (value: string | number) => void;
  className?: string;
  height?: number; // total visible height of the wheel
  itemHeight?: number; // height of each row
  formatLabel?: (option: PickerOption) => string;
  hideNativeHighlight?: boolean; // hide library's default 1px guide lines
  gap?: number; // extra vertical space between items (in px)
};

/**
 * Mobile wheel picker styled to match design tokens.
 * Wraps `react-mobile-picker` and overlays center highlight + edge gradients.
 */
export function Picker({
  name,
  options,
  value,
  onChange,
  className,
  itemHeight = 36,
  formatLabel,
  hideNativeHighlight = true,
  gap = 4,
}: PickerProps) {
  const display = (opt: PickerOption) =>
    formatLabel ? formatLabel(opt) : String(opt.label ?? opt.value);

  return (
    <div
      className={cn(
        'relative inline-flex w-[120px] oi-rmpicker',
        hideNativeHighlight && 'oi-rmpicker--no-lines',
        className,
      )}
    >
      {/* Top fade overlay (36px) */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-0 h-9 z-1"
        style={{
          background: 'linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0.20) 100%)',
        }}
      />
      {/* Bottom fade overlay (36px) */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 bottom-0 h-9 z-1"
        style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.20) 0%, #FFF 100%)',
        }}
      />

      <RMPicker
        wheelMode="natural"
        className={cn('w-full')}
        height={3 * (itemHeight + gap)}
        itemHeight={itemHeight + gap}
        value={{ [name]: value }}
        onChange={(v: Record<string, string | number>) =>
          onChange((v[name] as string | number) ?? '')
        }
      >
        <RMPicker.Column name={name}>
          {options.map(opt => (
            <RMPicker.Item key={opt.label} value={opt.value}>
              {({ selected }: { selected: boolean }) => (
                <div
                  className={cn(
                    'w-full text-center',
                    selected
                      ? 'text-body-1 rounded-full py-2 px-4 bg-secondary text-primary'
                      : 'text-body-2 text-secondary',
                  )}
                >
                  {display(opt)}
                </div>
              )}
            </RMPicker.Item>
          ))}
        </RMPicker.Column>
      </RMPicker>
    </div>
  );
}

export default Picker;
